import { BaseMarkerStateManager } from '../base/baseMarkerStateManager'
import { RectangleMarker } from './rectangleMarker'
import { SquareHandle } from '../handles/squareHandle'
import { type RectangleMarkerState, type Point, Axis, MarkerModes } from '../../types'

export class RectangleMarkerStateManager extends BaseMarkerStateManager<
  RectangleMarker,
  RectangleMarkerState,
  SquareHandle
> {
  public position: Point
  public dimensions: {
    height: number
    width: number
  }

  constructor(marker: RectangleMarker) {
    super(marker)
    this.marker = marker
    this.typeName = RectangleMarker.typeName
    this.position = { x: undefined, y: undefined }
    this.dimensions = { height: undefined, width: undefined }
  }

  /**
   * Moves the marker given the deltaX and deltaY of the mouse.
   * @param deltaX Change in mouse x position since last checked
   * @param deltaY Change in mouse y position since last checked
   */
  moveMarker(deltaX: number, deltaY: number) {
    this.position.x = this.position.x + deltaX
    this.position.y = this.position.y + deltaY
    this.marker.handles.populateHandlePositions()
    this.marker.markerEditCallback(this.marker)
  }

  /**
   * Resize marker based off the selected handle index
   * @param handleIndex Index of the handle in this.handles
   * @param deltaX Change in mouse position on x axis
   * @param deltaY Change in mouse position on y axis
   */
  resizeMarker(handleIndex: number, deltaX: number, deltaY: number) {
    switch (handleIndex) {
      case 0: //Top-left
        this.dimensions.width -= deltaX
        this.dimensions.height -= deltaY
        this.position.x += deltaX
        this.position.y += deltaY
        break
      case 1: //Top-right
        this.dimensions.width += deltaX
        this.dimensions.height -= deltaY
        this.position.y += deltaY
        break
      case 2: //Bottom-left
        this.dimensions.width -= deltaX
        this.dimensions.height += deltaY
        this.position.x += deltaX
        break
      case 3: //Bottom-right
        this.dimensions.width += deltaX
        this.dimensions.height += deltaY
        break
    }

    //TODO Improve handle tracking logic
    //Currently handles are tracked via their indices which is not ideal
    //If handles are rearranged from their original positions via inversing the shape
    //or any other behaviour in the future that may require more dynamic handle positions
    //you have to carefully reassign the indices such that they remain aligned
    //with top-left, top-right, bottom-left, bottom-right positiions.
    //Ideally this would be handled with a graph approach such as
    //the polygonmarkeredges class.
    //See gitlab ticket for additional details:
    //https://gitlab.com/near-earth/inspection/aci-portfolio/jeteye-review/-/issues/155
    //Handles horizonatal shape inversion
    if (this.dimensions.width < 0) {
      this.invertMarker(Axis.X)
    }
    //Handles vertircal shape inversion
    if (this.dimensions.height < 0) {
      this.invertMarker(Axis.Y)
    }
    this.marker.handles.populateHandlePositions()
    if (this.mode !== MarkerModes.Create) this.marker.markerEditCallback(this.marker)
  }

  public invertMarker(axis: Axis) {
    const topLeftHandle = this.handles[0]
    const topRightHandle = this.handles[1]
    const bottomLeftHandle = this.handles[2]
    const bottomRightHandle = this.handles[3]

    if (axis === Axis.X) {
      this.handles[0] = topRightHandle
      this.handles[1] = topLeftHandle
      this.handles[2] = bottomRightHandle
      this.handles[3] = bottomLeftHandle
      this.position.x = this.position.x + this.dimensions.width
      this.dimensions.width = Math.abs(this.dimensions.width)
    } else if (axis === Axis.Y) {
      this.handles[0] = bottomLeftHandle
      this.handles[1] = bottomRightHandle
      this.handles[2] = topLeftHandle
      this.handles[3] = topRightHandle
      this.position.y = this.position.y + this.dimensions.height
      this.dimensions.height = Math.abs(this.dimensions.height)
    }
  }

  public restore(state: RectangleMarkerState): void {
    super.restore(state)
    this.position = state.position
    this.dimensions = state.dimensions
    this.marker.handles.populate()
  }

  public export(): RectangleMarkerState {
    return Object.assign(
      {
        position: this.position,
        dimensions: this.dimensions,
      },
      super.export()
    )
  }
}
